export type ParsedVideoUrl = {
  provider: "youtube" | "vimeo" | "loom" | ""
  embedId: string
}

export type VideoEmbedOptions = {
  autoplay?: boolean
  muted?: boolean
}

export function normalizeVideoUrl(url = "") {
  const trimmedUrl = url.trim()
  if (!trimmedUrl || /^https?:\/\//i.test(trimmedUrl)) return trimmedUrl

  return `https://${trimmedUrl.replace(/^\/\//, "")}`
}

export function parseVideoUrl(url = ""): ParsedVideoUrl {
  const youtubePattern =
    /(?:youtube\.com\/(?:shorts\/|[^/\n\s]+\/\S+\/|(?:v|e(?:mbed)?)\/|\S*?[?&]v=)|youtu\.be\/)([a-zA-Z0-9_-]+)(?=[?&]|$)/
  const vimeoPattern =
    /vimeo\.com\/(?:channels\/(?:\w+\/)?|groups\/[^/]*\/videos\/|album\/\d+\/video\/|video\/|)(\d+)(?:$|\/|\?)/
  const loomPattern =
    /(?:https?:\/\/)?(?:www\.)?(?:loom\.com|useloom\.com)\/(?:share|embed)\/([a-zA-Z0-9]+)(?:$|[/?#])/

  const youtubeMatch = url.match(youtubePattern)
  if (youtubeMatch) {
    return {
      provider: "youtube",
      embedId: youtubeMatch[1]
    }
  }

  const vimeoMatch = url.match(vimeoPattern)
  if (vimeoMatch) {
    return {
      provider: "vimeo",
      embedId: vimeoMatch[1]
    }
  }

  const loomMatch = url.match(loomPattern)
  if (loomMatch) {
    return {
      provider: "loom",
      embedId: loomMatch[1]
    }
  }

  return {
    provider: "",
    embedId: ""
  }
}

export function isValidParsedVideo(video: ParsedVideoUrl) {
  if (!video.provider || !video.embedId) {
    return false
  }

  if (video.provider === "youtube") {
    return video.embedId.length === 11
  }

  if (video.provider === "vimeo") {
    return /^\d+$/.test(video.embedId)
  }

  if (video.provider === "loom") {
    return video.embedId.length >= 16 && video.embedId.length <= 64
  }

  return false
}

export function getVideoEmbedUrl(url = "", options: VideoEmbedOptions = {}) {
  const video = parseVideoUrl(url)

  if (video.provider === "youtube") {
    const query = [options.autoplay ? "autoplay=1" : "", options.muted ? "mute=1" : ""]
      .filter(Boolean)
      .join("&")

    return `https://www.youtube.com/embed/${video.embedId}${query ? `?${query}` : ""}`
  }

  if (video.provider === "vimeo") {
    return `https://player.vimeo.com/video/${video.embedId}`
  }

  if (video.provider === "loom") {
    return `https://www.loom.com/embed/${video.embedId}?hideEmbedTopBar=true&hide_share=true&hide_title=true&hide_owner=true`
  }

  return ""
}

export function getYoutubeThumbnailUrl(url = "") {
  const video = parseVideoUrl(url)
  if (video.provider !== "youtube" || !isValidParsedVideo(video)) return ""

  return `https://img.youtube.com/vi/${video.embedId}/hqdefault.jpg`
}

async function getOembedThumbnailUrl(oembedEndpoint: string, url: string) {
  const params = new URLSearchParams({
    url,
    format: "json"
  })

  try {
    const response = await fetch(`${oembedEndpoint}?${params.toString()}`)
    if (!response.ok) return ""

    const data = (await response.json()) as { thumbnail_url?: string }
    return data.thumbnail_url ?? ""
  } catch {
    return ""
  }
}

export async function getLoomThumbnailUrl(url = "") {
  const video = parseVideoUrl(url)
  if (video.provider !== "loom" || !url || !isValidParsedVideo(video)) return ""

  const thumbnailUrl = await getOembedThumbnailUrl("https://www.loom.com/v1/oembed", url)

  return thumbnailUrl.endsWith(".gif") ? thumbnailUrl.replace(/\.gif$/i, ".jpg") : thumbnailUrl
}

export async function getVimeoThumbnailUrl(url = "") {
  const video = parseVideoUrl(url)
  if (video.provider !== "vimeo" || !url || !isValidParsedVideo(video)) return ""

  return getOembedThumbnailUrl("https://vimeo.com/api/oembed.json", url)
}

export async function getExternalVideoThumbnailUrl(url = "") {
  const youtubeThumbnailUrl = getYoutubeThumbnailUrl(url)
  if (youtubeThumbnailUrl) return youtubeThumbnailUrl

  const video = parseVideoUrl(url)
  if (video.provider === "vimeo") return getVimeoThumbnailUrl(url)
  if (video.provider === "loom") return getLoomThumbnailUrl(url)

  return ""
}
